import { TEntityRef, TProject } from '@ws/shared/types';

import { WINDOW_AMOUNT_LIMIT } from '../../constants/contentWindow';
import { TWindowSettings } from '../../redux/contentWindows/contentWindows.types';

import { EMPTY_EDITOR_LAYOUT, TEditorLayout } from './editorLayout.types';

/**
 * Ссылка валидна, только если узел с таким id есть в проекте И его тип совпадает
 * с сохранённым. Проверять один id мало: `EditorPage` ищет узел в
 * `project[`${selected.type}s`]`, и ссылка с разъехавшимся типом промахивается мимо
 * словаря — экран навсегда залипает на сплеш-скрине.
 */
// Только собственные свойства: `notes['__proto__']` вернул бы Object.prototype,
// и мусорный id прошёл бы за живой узел — дальше он уезжает в URL и в рендер.
const hasNode = (pool: Record<string, unknown>, id: string): boolean =>
  Object.prototype.hasOwnProperty.call(pool, id);

function isLiveRef(ref: TEntityRef | null, project: TProject): boolean {
  if (!ref) {
    return false;
  }

  const pool = ref.type === 'note' ? project.notes : project.collections;

  return hasNode(pool, ref.id);
}

function sanitizeContentWindows(
  contentWindows: TEditorLayout['contentWindows'],
  project: TProject,
): TEditorLayout['contentWindows'] {
  const list = contentWindows.list
    // Плавающее окно бывает только для заметки — коллекцию в него не открыть.
    .filter((nodeId) => hasNode(project.notes, nodeId))
    // Элемент без настроек уронил бы WindowTabsPanel: он читает settings[nodeId].isOpen
    // без guard'а.
    .filter((nodeId) => hasNode(contentWindows.settings, nodeId))
    // Тот же лимит, что у редьюсера: восстановление не должно его нарушать.
    // Оставляем хвост — редьюсер тоже вытесняет самые старые, с головы.
    .slice(-WINDOW_AMOUNT_LIMIT);

  const settings = list.reduce<Record<string, TWindowSettings>>((acc, nodeId) => {
    acc[nodeId] = contentWindows.settings[nodeId];
    return acc;
  }, {});

  return { list, settings };
}

/**
 * Приводит сохранённый срез к тому, что действительно существует в проекте.
 *
 * GRASP Information Expert: вопрос «жив ли ещё этот узел» решается там, где есть
 * проект. Функция ничего не знает ни про localStorage, ни про redux, и всегда
 * возвращает валидный объект — в том числе на `null`.
 *
 * Гарантирует инвариант `Object.keys(settings) === list`.
 */
export function sanitizeLayout(layout: TEditorLayout | null, project: TProject): TEditorLayout {
  if (!layout) {
    return EMPTY_EDITOR_LAYOUT;
  }

  return {
    ...layout,
    selected: isLiveRef(layout.selected, project) ? layout.selected : null,
    secondarySelected: isLiveRef(layout.secondarySelected, project)
      ? layout.secondarySelected
      : null,
    contentWindows: sanitizeContentWindows(layout.contentWindows, project),
  };
}
